import { emptySplitApi as api } from "./emptyApi"
import {
  AccountInfoDto,
  CompanyConnectCardDto,
  CompanyConnectorDto,
  CompanyConnectorStatusDto,
  ConnectionLinkDto,
} from "./types"
const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    companyConnectorControllerCreateCompanyConnector: build.mutation<
      CompanyConnectorControllerCreateCompanyConnectorApiResponse,
      CompanyConnectorControllerCreateCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/company-connector`,
        method: "POST",
        body: queryArg.createCompanyConnectorDto,
      }),
    }),
    companyConnectorControllerReAuthCompanyConnector: build.mutation<
      CompanyConnectorControllerReAuthCompanyConnectorApiResponse,
      CompanyConnectorControllerReAuthCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/re-auth`,
        method: "POST",
        body: queryArg.reAuthCompanyConnectorDto,
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerGetCompanyConnectors: build.query<
      CompanyConnectorControllerGetCompanyConnectorsApiResponse,
      CompanyConnectorControllerGetCompanyConnectorsApiArg
    >({
      query: () => ({ url: `/company-connector` }),
      providesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerAuthenticateCompanyConnector: build.mutation<
      CompanyConnectorControllerAuthenticateCompanyConnectorApiResponse,
      CompanyConnectorControllerAuthenticateCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/authenticate`,
        method: "POST",
        body: queryArg.authenticateCompanyConnectorDto,
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerCompleteCompanyConnectorOnboarding:
      build.mutation<
        CompanyConnectorControllerCompleteCompanyConnectorOnboardingApiResponse,
        CompanyConnectorControllerCompleteCompanyConnectorOnboardingApiArg
      >({
        query: queryArg => ({
          url: `/company-connector/complete-onboard`,
          method: "PUT",
          body: queryArg.completeOnboardingDto,
        }),
        invalidatesTags: ["CompanyConnectors"],
      }),
    companyConnectorControllerUpdateCompanyConnector: build.mutation<
      CompanyConnectorControllerUpdateCompanyConnectorApiResponse,
      CompanyConnectorControllerUpdateCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateCompanyConnectorDto,
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerDeleteCompanyConnector: build.mutation<
      CompanyConnectorControllerDeleteCompanyConnectorApiResponse,
      CompanyConnectorControllerDeleteCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerResetSetup: build.mutation<
      CompanyConnectorDto,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/company-connector/${id}/reset-setup`,
        method: "POST",
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerUploadCsv: build.mutation<
      CompanyConnectorControllerUploadCsvApiResponse,
      CompanyConnectorControllerUploadCsvApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/upload-csv`,
        method: "POST",
        body: queryArg.uploadCsvDto,
      }),
    }),
    companyConnectorControllerGetCompanyConnectorLink: build.query<
      CompanyConnectorControllerGetCompanyConnectorLinkApiResponse,
      CompanyConnectorControllerGetCompanyConnectorLinkApiArg
    >({
      query: queryArg => ({ url: `/company-connector/${queryArg.id}/links` }),
    }),
    companyConnectorControllerGetCompanyConnectorAccountInfo: build.query<
      CompanyConnectorControllerGetCompanyConnectorAccountInfoApiResponse,
      CompanyConnectorControllerGetCompanyConnectorAccountInfoApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/account-info`,
      }),
    }),
    companyConnectorControllerGetCompanyConnectorStatus: build.query<
      CompanyConnectorControllerGetCompanyConnectorStatusApiResponse,
      CompanyConnectorControllerGetCompanyConnectorStatusApiArg
    >({
      query: queryArg => ({ url: `/company-connector/${queryArg.id}/status` }),
    }),
    companyConnectorControllerGetPresignedUrl: build.query<
      CompanyConnectorControllerCreatePresignedUrlApiResponse,
      CompanyConnectorControllerCreatePresignedUrlApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/presigned-url`,
      }),
    }),
    companyConnectorControllerAuthenticateUpload: build.mutation<
      CompanyConnectorControllerAuthenticateUploadApiResponse,
      CompanyConnectorControllerAuthenticateUploadApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/${queryArg.id}/authenticate-upload`,
        method: "POST",
        body: queryArg.authenticateUploadDto,
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    guestCompanyConnectorControllerCreateCompanyConnector: build.mutation<
      GuestCompanyConnectorControllerCreateCompanyConnectorApiResponse,
      GuestCompanyConnectorControllerCreateCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: "/guest/company-connector",
        method: "POST",
        body: {
          token: queryArg.token,
        },
      }),
    }),
    guestCompanyConnectorControllerAuthenticate: build.mutation<
      GuestCompanyConnectorControllerAuthenticateCompanyConnectorApiResponse,
      GuestCompanyConnectorControllerAuthenticateCompanyConnectorApiArg
    >({
      query: queryArg => ({
        url: `/guest/company-connector/${queryArg.id}/authenticate`,
        method: "POST",
        body: {
          token: queryArg.token,
          ...queryArg.authenticateCompanyConnectorDto,
        },
      }),
    }),
    companyConnectorControllerGetIgConnectorTags: build.query<
      CompanyConnectorControllerGetIgConnectorTagsApiResponse,
      CompanyConnectorControllerGetIgConnectorTagsApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/ig/tags`,
        params: { before: queryArg.before, after: queryArg.after },
      }),
    }),
    companyConnectorControllerGetGa4Properties: build.query<
      CompanyConnectorControllerGetGa4PropertiesApiResponse,
      CompanyConnectorControllerGetGa4PropertiesApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/ga4/${queryArg.id}/properties`,
      }),
    }),
    companyConnectorControllerSetGa4Property: build.mutation<
      CompanyConnectorControllerSetGa4PropertyApiResponse,
      CompanyConnectorControllerSetGa4PropertyApiArg
    >({
      query: queryArg => ({
        url: `/company-connector/ga4/${queryArg.id}/property`,
        method: "POST",
        body: queryArg.setGa4PropertyDto,
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerCompleteAirbyteSetup: build.mutation<
      CompanyConnectorDto,
      { id: string; additionalConfig: Record<string, unknown> }
    >({
      query: ({ id, additionalConfig }) => ({
        url: `/company-connector/${id}/complete-airbyte-setup`,
        method: "POST",
        body: { additionalConfig },
      }),
      invalidatesTags: ["CompanyConnectors"],
    }),
    companyConnectorControllerGetAirbyteProperties: build.query<
      {
        additional_config?: Array<{
          hidden?: boolean
          format: string
          property_label: string
          configuration_path: string
          config_phase?: "oauth" | "source"
          info?: string
          guide?: string
          property_type_field?: string
          property_type_options?: Record<
            string,
            {
              prefix?: string
              label: string
              default?: boolean
              help_text?: string
            }
          >
          pattern?: string
          pattern_message?: string
        }>
        metadata?: Record<string, unknown>
      },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/company-connector/${id}/airbyte-properties`,
      }),
    }),
    companyConnectorControllerGetAirbyteSetupError: build.query<
      { message: string } | null,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/company-connector/${id}/airbyte-setup-error`,
      }),
    }),
  }),
  overrideExisting: false,
})

export type GuestCompanyConnectorControllerCreateCompanyConnectorApiResponse =
  CompanyConnectCardDto
export type GuestCompanyConnectorControllerCreateCompanyConnectorApiArg = {
  token: string
}

export type GuestCompanyConnectorControllerAuthenticateCompanyConnectorApiResponse =
  CompanyConnectorDto
export type GuestCompanyConnectorControllerAuthenticateCompanyConnectorApiArg =
  {
    id: string
    token: string
    authenticateCompanyConnectorDto: AuthenticateCompanyConnectorDto
  }

export type CompanyConnectorControllerGetIgConnectorTagsApiResponse =
  /** status 200  */ TagInsightsResponseDto
export type CompanyConnectorControllerGetIgConnectorTagsApiArg = {
  before?: string
  after?: string
}
export { injectedRtkApi as generatedApi }
export type CompanyConnectorControllerCreateCompanyConnectorApiResponse =
  /** status 200  */ CompanyConnectCardDto
export type CompanyConnectorControllerCreateCompanyConnectorApiArg = {
  createCompanyConnectorDto: CreateCompanyConnectorDto
}
export type CompanyConnectorControllerReAuthCompanyConnectorApiResponse =
  /** status 201  */ CompanyConnectCardDto
export type CompanyConnectorControllerReAuthCompanyConnectorApiArg = {
  id: string
  reAuthCompanyConnectorDto: ReAuthCompanyConnectorDto
}
export type CompanyConnectorControllerGetCompanyConnectorsApiResponse =
  /** status 200  */ CompanyConnectorDto[]
export type CompanyConnectorControllerGetCompanyConnectorsApiArg = void
export type CompanyConnectorControllerAuthenticateCompanyConnectorApiResponse =
  /** status 200  */ CompanyConnectorDto
export type CompanyConnectorControllerAuthenticateCompanyConnectorApiArg = {
  id: string
  authenticateCompanyConnectorDto: AuthenticateCompanyConnectorDto
}
export type CompanyConnectorControllerCompleteCompanyConnectorOnboardingApiResponse =
  /** status 200  */ CompanyConnectorDto
export type CompanyConnectorControllerCompleteCompanyConnectorOnboardingApiArg =
  {
    completeOnboardingDto: CompleteOnboardingDto
  }
export type CompanyConnectorControllerUpdateCompanyConnectorApiResponse =
  /** status 200  */ CompanyConnectorDto
export type CompanyConnectorControllerUpdateCompanyConnectorApiArg = {
  id: string
  updateCompanyConnectorDto: UpdateCompanyConnectorDto
}
export type CompanyConnectorControllerDeleteCompanyConnectorApiResponse =
  /** status 200  */ CompanyConnectorDto
export type CompanyConnectorControllerDeleteCompanyConnectorApiArg = {
  id: string
}
export type CompanyConnectorControllerUploadCsvApiResponse =
  /** status 200  */ UploadCsvResponseDto
export type CompanyConnectorControllerUploadCsvApiArg = {
  id: string
  uploadCsvDto: UploadCsvDto
}
export type CompanyConnectorControllerGetCompanyConnectorLinkApiResponse =
  /** status 200  */ ConnectionLinkDto
export type CompanyConnectorControllerGetCompanyConnectorLinkApiArg = {
  id: string
}
export type CompanyConnectorControllerGetCompanyConnectorAccountInfoApiResponse =
  AccountInfoDto
export type CompanyConnectorControllerGetCompanyConnectorAccountInfoApiArg = {
  id: string
}
export type CompanyConnectorControllerGetCompanyConnectorStatusApiResponse =
  /** status 200  */ CompanyConnectorStatusDto
export type CompanyConnectorControllerGetCompanyConnectorStatusApiArg = {
  id: string
}
export type CompanyConnectorControllerCreatePresignedUrlApiResponse = {
  key: string
  presignedUrl: string
}
export type CompanyConnectorControllerCreatePresignedUrlApiArg = {
  id: string
}
export type CompanyConnectorControllerAuthenticateUploadApiResponse =
  CompanyConnectorDto
export type CompanyConnectorControllerAuthenticateUploadApiArg = {
  id: string
  authenticateUploadDto: AuthenticateUploadDto
}

export type CreateCompanyConnectorDto = {
  service: string
  preConfig?: Record<string, unknown>
  /** Force creating a new connection even if one already exists for this service */
  forceNew?: boolean
}
export type ReAuthCompanyConnectorDto = {
  /** Pre-configuration values for OAuth connectors (collected before OAuth initiation) */
  preConfig?: Record<string, unknown>
}
export type CreatePresignedUrlDto = {
  connectorId: string
}
export type AuthenticateCompanyConnectorDto = {
  authCode: string
  extra?: string
  extraMetadata?: Record<string, unknown>
}
export type CompleteOnboardingDto = {
  fivetranId: string
}
export type UpdateCompanyConnectorDto = {
  metadata?: object
  confirmedAt?: string
}
export type UploadCsvResponseDto = {
  status: string
  result: object[]
}
export type UploadCsvDto = {
  files: Blob[][]
}
export type AuthenticateUploadDto = {
  keys: string[]
}
export type TagInsightItemDto = {
  caption: string
  comments_count: number
  id: string
  like_count: number
  media_product_type: string
  media_type: string
  timestamp: string
  username: string
  permalink: string
}
export type TagInsightCursorsDto = {
  before?: string
  after?: string
}
export type TagInsightPagingDto = {
  cursors: TagInsightCursorsDto
  next?: string
}
export type TagInsightsResponseDto = {
  data: TagInsightItemDto[]
  paging: TagInsightPagingDto
}

export type GA4PropertyDto = {
  accountName: string
  propertyName: string
  propertyId: string
}

export type GA4PropertiesResponseDto = {
  data: GA4PropertyDto[]
}

export type CompanyConnectorControllerGetGa4PropertiesApiResponse =
  GA4PropertiesResponseDto

export type CompanyConnectorControllerGetGa4PropertiesApiArg = {
  id: string
}

export type SetGa4PropertyDto = {
  propertyId: string
}

export type CompanyConnectorControllerSetGa4PropertyApiResponse = void

export type CompanyConnectorControllerSetGa4PropertyApiArg = {
  id: string
  setGa4PropertyDto: SetGa4PropertyDto
}

export const {
  useCompanyConnectorControllerCreateCompanyConnectorMutation,
  useCompanyConnectorControllerReAuthCompanyConnectorMutation,
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
  useCompanyConnectorControllerAuthenticateCompanyConnectorMutation,
  useCompanyConnectorControllerCompleteCompanyConnectorOnboardingMutation,
  useCompanyConnectorControllerUpdateCompanyConnectorMutation,
  useCompanyConnectorControllerDeleteCompanyConnectorMutation,
  useCompanyConnectorControllerResetSetupMutation,
  useLazyCompanyConnectorControllerGetPresignedUrlQuery,
  useCompanyConnectorControllerUploadCsvMutation,
  useCompanyConnectorControllerGetCompanyConnectorLinkQuery,
  useCompanyConnectorControllerGetCompanyConnectorAccountInfoQuery,
  useCompanyConnectorControllerGetCompanyConnectorStatusQuery,
  useCompanyConnectorControllerAuthenticateUploadMutation,
  useGuestCompanyConnectorControllerCreateCompanyConnectorMutation,
  useGuestCompanyConnectorControllerAuthenticateMutation,
  useCompanyConnectorControllerGetIgConnectorTagsQuery,
  useCompanyConnectorControllerGetGa4PropertiesQuery,
  useCompanyConnectorControllerSetGa4PropertyMutation,
  useCompanyConnectorControllerCompleteAirbyteSetupMutation,
  useCompanyConnectorControllerGetAirbytePropertiesQuery,
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
} = injectedRtkApi
